import { useInterrupted, useRoute } from '@composables'
import useHandleError from '@errors/errorHandler.js'
import { readonly, ref } from 'vue'

export default function useAsync(
  cb = () => {},
  routeHandlerOrErrorMessage = null,
  cancelPrevious = false
) {
  const { routePath } = useRoute()
  const { handleError } = useHandleError()
  // If the api request was made in a different route to it being completed
  // log the new path so it can be checked to stop code running using interrupt()
  const { startInterrupt, endInterrupt } = useInterrupted(routePath.value)
  const data = ref(null)
  const errorState = ref(null)
  const errorCodeState = ref(null)
  const errorRefState = ref(null)
  const loading = ref(false)

  let abortController = new AbortController()
  let firstRun = true
  let aborted = false

  const run = async (...args) => {
    try {
      if (cancelPrevious && !firstRun) {
        abortController.abort()
        abortController = new AbortController()
      }

      firstRun = false
      errorState.value = null
      loading.value = true
      aborted = false
      startInterrupt(routePath.value)
      data.value = cancelPrevious
        ? await cb(...args, abortController.signal)
        : await cb(...args)
      endInterrupt(routePath.value)
    } catch (error) {
      if (error.name === 'CanceledError') {
        aborted = true
      } else {
        handleError({
          error,
          errorState,
          errorCodeState,
          errorRefState,
          routeHandlerOrErrorMessage,
        })
      }
    } finally {
      loading.value = aborted
      aborted = false
    }
  }

  function setError(error) {
    errorState.value = error
  }

  return {
    errorCode: readonly(errorCodeState),
    errorRef: readonly(errorRefState),
    error: readonly(errorState),
    setError,
    loading,
    data,
    run,
  }
}
